import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import { useSelector, useDispatch } from "react-redux";
import Select from "react-select";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import { setVisibleColumns, setSortOrder, setSortBy, setFilterField } from "../../app/itemGridPreferencesSlice";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import APIs from "../../Constants/apis";
import axiosInstance from "../../config/axiosApi/axiosApi";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import { SortOrderDropDownSelection } from "../../components/CustomComponents/DropdownSelections/DropDownSelections";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";
import "../order/ManageOrderFilters.css";
import { useProfileAndSettings } from "../../hooks/useProfileAndSettings";
import moment from "moment";
import Pagination from "../../components/CustomComponents/Pagination/pagination";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import InventoryReportPeriodPicker from "../../components/CustomComponents/InventoryReportPeriodPicker/InventoryReportPeriodPicker";
import {
  DATE_PRESET,
  getDateRangeForPreset,
  getEffectiveReportDateBounds,
} from "./inventoryReportDateUtils";
import {
  formatCompanyDateTimeForApi,
  getCompanyNowDate,
  parseCompanyDateTimeLocal,
} from "../../utils/companyDateTime";

/** Heavy ledger aggregation — allow longer than default axios timeout (30s). */
const MASTER_INVENTORY_REPORT_TIMEOUT_MS = 120000;

const MasterInventoryReport = () => {
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const user = useSelector((state) => state.auth.user);
  const { profileData } = useSelector((state) => state.auth);
  const userIdentity = user?.userData?.userId ?? user?.userData?.email ?? CompID;
  const parentCompId = profileData?.parentComID ?? profileData?.ParentComID ?? 0;
  const isMain = !parentCompId || parseInt(parentCompId, 10) <= 0;

  const pageKey = "masterInventoryReport";
  const [pageNumber, setPageNumber] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const searchTimeoutRef = useRef(null);

  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);

  // New column structure: Opening + IN movements - OUT movements = Current
  const defaultVisibleColumns = {
    itemCode: true,
    itemName: true,
    categoryName: true,
    unitOfMeasure: false,
    openingStock: true,
    // Stock IN
    purchaseQty: true,
    transferInQty: true,
    adjustmentInQty: false,
    recipeBatchInQty: false,
    saleReturnInQty: false,
    totalIn: true,
    // Stock OUT (sales + production consumption combined)
    salesAndProductionQty: true,
    transferOutQty: true,
    adjustmentOutQty: false,
    totalOut: true,
    // Closing
    currentStock: true,
    costPerUnit: false,
    stockValue: true,
  };

  const defaultSortOrder = { value: "ASC", label: "ASC" };
  const defaultSortBy = { value: "ItemName", label: "Item Name" };
  const defaultPageSize = { value: "20", label: "20" };

  const parentIdForLocation = isMain && CompID != null ? (typeof CompID === "number" ? CompID : parseInt(CompID, 10)) : null;
  const companyName = profileData?.compName ?? profileData?.CompName ?? "";
  const defaultLocation = useMemo(
    () =>
      isMain && parentIdForLocation != null && !isNaN(parentIdForLocation)
        ? { value: parentIdForLocation, label: companyName || "This branch only" }
        : { value: "all", label: "All Locations" },
    [isMain, parentIdForLocation, companyName]
  );

  const { getCompCurrency, getCompTimeZone, getCompUTCTimeZone, hasInitiallyLoaded } = useProfileAndSettings();
  const compCurrency = getCompCurrency();
  const compTimeZone = getCompTimeZone();
  const compUTCTimeZone = getCompUTCTimeZone();

  const companyTzOptions = useMemo(
    () => ({ compTimeZone, compUTCTimeZone }),
    [compTimeZone, compUTCTimeZone]
  );

  const defaultDateRange = useMemo(
    () =>
      getDateRangeForPreset(DATE_PRESET.LAST_1, companyTzOptions) || [
        getCompanyNowDate(),
        getCompanyNowDate(),
      ],
    [companyTzOptions]
  );

  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const mergedVisibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const visibleColumns = useMemo(() => {
    const cols = { ...mergedVisibleColumns };
    const saved = preferences.visibleColumns;
    if (saved && ("salesQty" in saved || "productionOutQty" in saved)) {
      cols.salesAndProductionQty = saved.salesQty !== false || saved.productionOutQty !== false;
    }
    return cols;
  }, [mergedVisibleColumns, preferences.visibleColumns]);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = useMemo(() => {
    const sb = preferences.sortBy || defaultSortBy;
    if (sb?.value === "ProductionOutQty") {
      return { value: "SalesQty", label: "Sales & Production" };
    }
    return sb;
  }, [preferences.sortBy]);
  const location = preferences.location || defaultLocation;
  const dateRange = preferences.dateRange || defaultDateRange;
  const dateRangePresetStored = preferences.dateRangePreset ?? DATE_PRESET.LAST_1;
  const dateRangePreset =
    dateRangePresetStored === "custom" ? DATE_PRESET.LAST_1 : dateRangePresetStored;
  const search = preferences.search ?? "";
  const pageSize = preferences.pageSize || defaultPageSize;
  const [rawStartDate, rawEndDate] = dateRange;

  const dispatch = useDispatch();
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const themeColor = useThemeColor();

  const currencySymbols = { GBP: "£", USD: "$", EUR: "€" };
  const getCurrencySymbol = (code) => currencySymbols[code] || code;

  const sortByOptions = [
    { value: "ItemCode", label: "Item Code" },
    { value: "ItemName", label: "Item Name" },
    { value: "CategoryName", label: "Category" },
    { value: "OpeningStock", label: "Opening Stock" },
    { value: "PurchaseQty", label: "Purchase" },
    { value: "TransferInQty", label: "Transfer In" },
    { value: "AdjustmentInQty", label: "Adjust In" },
    { value: "RecipeBatchInQty", label: "Batch Prod In" },
    { value: "SaleReturnInQty", label: "Sale Return In" },
    { value: "TotalIn", label: "Total In" },
    { value: "SalesQty", label: "Sales & Production" },
    { value: "TransferOutQty", label: "Transfer Out" },
    { value: "AdjustmentOutQty", label: "Adjust Out" },
    { value: "TotalOut", label: "Total Out" },
    { value: "CurrentStock", label: "Current Stock" },
    { value: "CostPerUnit", label: "Cost/Unit" },
    { value: "StockValue", label: "Stock Value" },
  ];

  const masterInventoryColumnDefinitions = {
    itemCode: "Item Code",
    itemName: "Item Name",
    categoryName: "Category",
    unitOfMeasure: "Unit",
    openingStock: "Opening",
    purchaseQty: "Purchase",
    transferInQty: "Transfer In",
    adjustmentInQty: "Adjust In",
    recipeBatchInQty: "Batch Prod In",
    saleReturnInQty: "Sale Return In",
    totalIn: "Total In",
    salesAndProductionQty: "Sales & Production",
    transferOutQty: "Transfer Out",
    adjustmentOutQty: "Adjust Out",
    totalOut: "Total Out",
    currentStock: "Current",
    costPerUnit: "Cost/Unit",
    stockValue: "Value",
  };

  const locationOptions = useMemo(() => {
    if (locations.length > 0) return locations;
    if (isMain && parentIdForLocation != null && !isNaN(parentIdForLocation)) {
      return [{ value: parentIdForLocation, label: companyName || "This branch only" }];
    }
    return [];
  }, [locations, isMain, parentIdForLocation, companyName]);

  const resolvedLocation = useMemo(() => {
    const current = location ?? defaultLocation;
    if (!current?.value) return defaultLocation;
    const match = locationOptions.find((opt) => opt.value === current.value);
    return match ?? current;
  }, [location, locationOptions, defaultLocation]);

  const handleFilterChange = (filterName, selectedOption) => {
    dispatch(setFilterField({ pageKey, field: filterName, value: selectedOption }));
    setPageNumber(1);
  };

  const resetFilters = () => {
    dispatch(setFilterField({ pageKey, field: "location", value: defaultLocation }));
    dispatch(setFilterField({ pageKey, field: "dateRangePreset", value: DATE_PRESET.LAST_1 }));
    dispatch(setFilterField({ pageKey, field: "dateRange", value: defaultDateRange }));
    dispatch(setFilterField({ pageKey, field: "search", value: "" }));
    dispatch(setFilterField({ pageKey, field: "pageSize", value: defaultPageSize }));
  };

  const clearAllFilters = () => {
    resetFilters();
    setPageNumber(1);
  };

  const handleSearch = () => {
    setPageNumber(1);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    fetchReportData();
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    dispatch(setFilterField({ pageKey, field: "search", value }));
    setPageNumber(1);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => fetchReportData(), 500);
  };

  const applyDatePreset = (presetId) => {
    if (presetId === DATE_PRESET.START_TO_NOW) {
      const { startDate } = getEffectiveReportDateBounds(
        DATE_PRESET.START_TO_NOW,
        rawStartDate,
        rawEndDate,
        companyTzOptions
      );
      dispatch(setFilterField({ pageKey, field: "dateRange", value: [startDate, getCompanyNowDate()] }));
      dispatch(setFilterField({ pageKey, field: "dateRangePreset", value: DATE_PRESET.START_TO_NOW }));
      setPageNumber(1);
      return;
    }
    const range = getDateRangeForPreset(presetId, companyTzOptions);
    if (range) {
      dispatch(setFilterField({ pageKey, field: "dateRange", value: range }));
      dispatch(setFilterField({ pageKey, field: "dateRangePreset", value: presetId }));
      setPageNumber(1);
    }
  };

  const handleStartToNowInputChange = (e) => {
    const v = e.target.value;
    if (!v) return;
    const start =
      parseCompanyDateTimeLocal(`${v}T00:00`, companyTzOptions) ||
      moment(v, "YYYY-MM-DD", true).startOf("day").toDate();
    dispatch(setFilterField({ pageKey, field: "dateRange", value: [start, getCompanyNowDate()] }));
    dispatch(setFilterField({ pageKey, field: "dateRangePreset", value: DATE_PRESET.START_TO_NOW }));
    setPageNumber(1);
  };

  const applyCustomStart = (customStartLocal) => {
    const start = parseCompanyDateTimeLocal(customStartLocal, companyTzOptions);
    if (!start) return;
    dispatch(setFilterField({ pageKey, field: "dateRange", value: [start, getCompanyNowDate()] }));
    dispatch(setFilterField({ pageKey, field: "dateRangePreset", value: DATE_PRESET.CUSTOM_RANGE }));
    setPageNumber(1);
  };

  const clearQuickSelectToDefault = () => {
    dispatch(setFilterField({ pageKey, field: "dateRangePreset", value: DATE_PRESET.LAST_1 }));
    dispatch(setFilterField({ pageKey, field: "dateRange", value: defaultDateRange }));
    setPageNumber(1);
  };

  const fetchReportData = useCallback(async () => {
    setLoading(true);
    try {
      const { startDate, endDate } = getEffectiveReportDateBounds(
        dateRangePreset,
        rawStartDate,
        rawEndDate,
        companyTzOptions
      );
      const formattedStartDate = formatCompanyDateTimeForApi(startDate, companyTzOptions);
      const formattedEndDate = formatCompanyDateTimeForApi(endDate, companyTzOptions);

      let locationFilterValue = null;
      if (isMain && resolvedLocation.value !== "all" && resolvedLocation.value != null) {
        const locValue = typeof resolvedLocation.value === "number" ? resolvedLocation.value : parseInt(resolvedLocation.value, 10);
        if (!isNaN(locValue) && locValue > 0) locationFilterValue = locValue;
      }

      const params = {
        CompID,
        StartDate: formattedStartDate,
        EndDate: formattedEndDate,
        LocationFilter: locationFilterValue,
        SearchTerm: search?.trim() || null,
        SortBy: sortBy.value,
        SortOrder: sortOrder.value,
        PageNumber: pageNumber,
        PageSize: pageSize.value,
      };

      const response = await axiosInstance.get(APIs.GET_MASTER_INVENTORY_REPORT, {
        params,
        timeout: MASTER_INVENTORY_REPORT_TIMEOUT_MS,
      });
      const data = response.data?.data ?? response.data ?? [];
      const totalCount = response.data?.totalRecords ?? response.data?.TotalRecords ?? 0;
      const pages = response.data?.totalPages ?? response.data?.TotalPages ?? 0;

      setReportData(Array.isArray(data) ? data : []);
      setTotalRecords(totalCount);
      setTotalPages(pages);
    } catch (error) {
      console.error("Error fetching Inventory report:", error);
      const isTimeout =
        error.code === "ECONNABORTED" ||
        (typeof error.message === "string" && error.message.toLowerCase().includes("timeout"));
      showErrorToast(
        isTimeout
          ? "Inventory report is taking longer than expected. Try a shorter date range or contact support if this continues."
          : error.response?.data?.message || error.message || "Failed to fetch Inventory report."
      );
      setReportData([]);
      setTotalRecords(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [CompID, isMain, search, sortBy, sortOrder, pageNumber, pageSize, rawStartDate, rawEndDate, dateRangePreset, resolvedLocation, companyTzOptions]);

  const fetchAllReportDataForExport = useCallback(async () => {
    const { startDate, endDate } = getEffectiveReportDateBounds(
      dateRangePreset,
      rawStartDate,
      rawEndDate,
      companyTzOptions
    );
    const formattedStartDate = formatCompanyDateTimeForApi(startDate, companyTzOptions);
    const formattedEndDate = formatCompanyDateTimeForApi(endDate, companyTzOptions);
    let locationFilterValue = null;
    if (isMain && resolvedLocation.value !== "all" && resolvedLocation.value != null) {
      const locValue = typeof resolvedLocation.value === "number" ? resolvedLocation.value : parseInt(resolvedLocation.value, 10);
      if (!isNaN(locValue) && locValue > 0) locationFilterValue = locValue;
    }
    const exportSize = Math.max(1, parseInt(totalRecords, 10) || 99999);
    const params = {
      CompID,
      StartDate: formattedStartDate,
      EndDate: formattedEndDate,
      LocationFilter: locationFilterValue,
      SearchTerm: search?.trim() || null,
      SortBy: sortBy.value,
      SortOrder: sortOrder.value,
      PageNumber: 1,
      PageSize: exportSize,
    };
    const response = await axiosInstance.get(APIs.GET_MASTER_INVENTORY_REPORT, {
      params,
      timeout: MASTER_INVENTORY_REPORT_TIMEOUT_MS,
    });
    const data = response.data?.data ?? response.data ?? [];
    return Array.isArray(data) ? data : [];
  }, [CompID, isMain, search, sortBy, sortOrder, rawStartDate, rawEndDate, dateRangePreset, resolvedLocation, totalRecords, companyTzOptions]);

  const fetchLocations = useCallback(async () => {
    if (!isMain || !CompID) return;
    try {
      setLoadingLocations(true);
      const response = await axiosInstance.get(APIs.GET_COMPANY_LOCATIONS, { params: { CompID } });
      const locationData = response.data?.data ?? response.data ?? [];
      const arr = Array.isArray(locationData) ? locationData : [];
      const parentId = typeof CompID === "number" ? CompID : parseInt(CompID, 10);
      const base = [{ value: parentId, label: companyName || "This branch only" }];
      const branchOpts = arr
        .filter((loc) => {
          const pid = loc.ParentComID ?? loc.parentComID;
          const id = loc.CompID ?? loc.compID;
          const pidNum = pid != null ? (typeof pid === "number" ? pid : parseInt(pid, 10)) : null;
          const idNum = id != null ? (typeof id === "number" ? id : parseInt(id, 10)) : null;
          return idNum != null && !isNaN(idNum) && pidNum === parentId && idNum !== parentId;
        })
        .map((loc) => ({
          value: (() => {
            const id = loc.CompID ?? loc.compID;
            return typeof id === "number" ? id : parseInt(id, 10);
          })(),
          label: String(loc.CompName ?? loc.compName ?? "").trim() || `Branch ${loc.CompID ?? loc.compID ?? ""}`,
        }))
        .filter((o) => o.value != null && !isNaN(o.value));
      setLocations([...base, ...branchOpts]);
    } catch (error) {
      console.error("Error fetching locations:", error);
      showErrorToast(error.response?.data?.message || "Error loading locations");
      const parentId = typeof CompID === "number" ? CompID : parseInt(CompID, 10);
      if (!isNaN(parentId)) {
        setLocations([{ value: parentId, label: companyName || "This branch only" }]);
      }
    } finally {
      setLoadingLocations(false);
    }
  }, [CompID, isMain, companyName]);

  const [isInitialized, setIsInitialized] = useState(false);
  const isFirstRender = useRef(true);
  const hasInitialFetch = useRef(false);
  const preferencesInitialized = useRef(false);
  const prevUserIdentityRef = useRef(null);
  const skipFilterFetchOnceRef = useRef(false);

  useEffect(() => {
    if (preferencesInitialized.current) return;
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
      dispatch(setVisibleColumns({ pageKey, columns: mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns) }));
    }
    if (!preferences.sortOrder) dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    if (!preferences.sortBy) dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    if (!preferences.dateRange) dispatch(setFilterField({ pageKey, field: "dateRange", value: defaultDateRange }));
    if (preferences.dateRangePreset === undefined) dispatch(setFilterField({ pageKey, field: "dateRangePreset", value: DATE_PRESET.LAST_1 }));
    if (preferences.dateRangePreset === "custom") {
      dispatch(setFilterField({ pageKey, field: "dateRangePreset", value: DATE_PRESET.LAST_1 }));
      dispatch(setFilterField({ pageKey, field: "dateRange", value: defaultDateRange }));
    }
    if (preferences.search === undefined) dispatch(setFilterField({ pageKey, field: "search", value: "" }));
    if (!preferences.pageSize) dispatch(setFilterField({ pageKey, field: "pageSize", value: defaultPageSize }));
    preferencesInitialized.current = true;
  }, [dispatch, pageKey, defaultDateRange, preferences.visibleColumns, preferences.sortOrder, preferences.sortBy, preferences.dateRange, preferences.dateRangePreset, preferences.search, preferences.pageSize]);

  // Set default location only after profile is loaded so the label is the real branch name
  useEffect(() => {
    if (!hasInitiallyLoaded || !CompID) return;
    if (!preferences.location) {
      dispatch(setFilterField({ pageKey, field: "location", value: defaultLocation }));
    }
  }, [hasInitiallyLoaded, CompID, preferences.location, defaultLocation, dispatch, pageKey]);

  const preferencesReady =
    preferences.visibleColumns &&
    preferences.sortOrder &&
    preferences.sortBy &&
    preferences.location &&
    preferences.dateRange &&
    preferences.pageSize;

  useEffect(() => {
    if (!hasInitiallyLoaded || !CompID || !preferencesReady) return;
    if (hasInitialFetch.current) return;
    hasInitialFetch.current = true;
    skipFilterFetchOnceRef.current = true;
    setIsInitialized(true);
    fetchReportData();
  }, [hasInitiallyLoaded, CompID, preferencesReady, fetchReportData]);

  useEffect(() => {
    if (!isInitialized || isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (skipFilterFetchOnceRef.current) {
      skipFilterFetchOnceRef.current = false;
      return;
    }
    if (!hasInitialFetch.current) return;
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    const timeoutId = setTimeout(() => fetchReportData(), 300);
    return () => clearTimeout(timeoutId);
  }, [resolvedLocation, rawStartDate, rawEndDate, dateRangePreset, isInitialized, fetchReportData]);

  useEffect(() => {
    if (isMain && hasInitiallyLoaded) fetchLocations();
  }, [isMain, hasInitiallyLoaded, fetchLocations]);

  // Persisted "All Locations" is no longer offered; map to first concrete location
  useEffect(() => {
    if (!hasInitiallyLoaded || !isMain || location?.value !== "all") return;
    const first = locationOptions[0];
    if (first) {
      dispatch(setFilterField({ pageKey, field: "location", value: first }));
    }
  }, [hasInitiallyLoaded, isMain, location?.value, locationOptions, dispatch, pageKey]);

  // Keep stored location label in sync when profile/locations load (e.g. after hard refresh)
  useEffect(() => {
    if (!hasInitiallyLoaded || !location?.value || location.value === "all") return;
    const match = locationOptions.find((opt) => opt.value === location.value);
    if (match && match.label !== location.label) {
      dispatch(setFilterField({ pageKey, field: "location", value: match }));
    }
  }, [hasInitiallyLoaded, location?.value, location?.label, locationOptions, dispatch, pageKey]);

  // Reset location only when the signed-in user/account actually changes — not on every mount/refresh
  useEffect(() => {
    if (!hasInitiallyLoaded || !CompID) return;
    if (prevUserIdentityRef.current == null) {
      prevUserIdentityRef.current = userIdentity;
      return;
    }
    if (prevUserIdentityRef.current === userIdentity) return;
    prevUserIdentityRef.current = userIdentity;
    dispatch(setFilterField({ pageKey, field: "location", value: defaultLocation }));
    if (isMain) fetchLocations();
  }, [userIdentity, CompID, isMain, hasInitiallyLoaded, defaultLocation, dispatch, pageKey, fetchLocations]);

  useEffect(() => {
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, []);

  const paginationFirstRun = useRef(true);
  useEffect(() => {
    if (!isInitialized || !hasInitialFetch.current) return;
    if (paginationFirstRun.current) {
      paginationFirstRun.current = false;
      return;
    }
    fetchReportData();
  }, [pageNumber, pageSize, sortBy, sortOrder, isInitialized]);

  const formatDateInCompanyTz = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return "-";
      return new Intl.DateTimeFormat("en-GB", {
        dateStyle: "short",
        timeZone: compTimeZone || "UTC",
      }).format(d);
    } catch {
      return new Date(dateStr).toLocaleDateString();
    }
  };

  const formatQuantity = (val) => {
    if (val == null || (typeof val !== "number" && isNaN(parseFloat(val)))) return "-";
    const n = parseFloat(val);
    return Number.isInteger(n) ? n.toString() : n.toFixed(2);
  };

  const getSalesAndProductionQty = (row) => {
    const sales = parseFloat(row.salesQty ?? row.SalesQty ?? 0);
    const production = parseFloat(row.productionOutQty ?? row.ProductionOutQty ?? 0);
    const total = (Number.isNaN(sales) ? 0 : sales) + (Number.isNaN(production) ? 0 : production);
    return total;
  };

  const columnKeys = [
    "itemCode", "itemName", "categoryName", "unitOfMeasure", "openingStock",
    "purchaseQty", "transferInQty", "adjustmentInQty", "recipeBatchInQty", "saleReturnInQty", "totalIn",
    "salesAndProductionQty", "transferOutQty", "adjustmentOutQty", "totalOut",
    "currentStock", "costPerUnit", "stockValue",
  ];

  const getExportData = (data = reportData) => {
    const headers = columnKeys.filter((k) => visibleColumns[k]).map((k) => masterInventoryColumnDefinitions[k]);
    const rows = data.map((row) => {
      const cells = [];
      if (visibleColumns.itemCode) cells.push(row.itemCode ?? row.ItemCode ?? "-");
      if (visibleColumns.itemName) cells.push(row.itemName ?? row.ItemName ?? "-");
      if (visibleColumns.categoryName) cells.push(row.categoryName ?? row.CategoryName ?? "-");
      if (visibleColumns.unitOfMeasure) cells.push(row.unitOfMeasure ?? row.UnitOfMeasure ?? "-");
      if (visibleColumns.openingStock) cells.push(formatQuantity(row.openingStock ?? row.OpeningStock));
      // Stock IN
      if (visibleColumns.purchaseQty) cells.push(formatQuantity(row.purchaseQty ?? row.PurchaseQty));
      if (visibleColumns.transferInQty) cells.push(formatQuantity(row.transferInQty ?? row.TransferInQty));
      if (visibleColumns.adjustmentInQty) cells.push(formatQuantity(row.adjustmentInQty ?? row.AdjustmentInQty));
      if (visibleColumns.recipeBatchInQty) cells.push(formatQuantity(row.recipeBatchInQty ?? row.RecipeBatchInQty));
      if (visibleColumns.saleReturnInQty) cells.push(formatQuantity(row.saleReturnInQty ?? row.SaleReturnInQty));
      if (visibleColumns.totalIn) cells.push(formatQuantity(row.totalIn ?? row.TotalIn));
      // Stock OUT
      if (visibleColumns.salesAndProductionQty) {
        cells.push(formatQuantity(getSalesAndProductionQty(row)));
      }
      if (visibleColumns.transferOutQty) cells.push(formatQuantity(row.transferOutQty ?? row.TransferOutQty));
      if (visibleColumns.adjustmentOutQty) cells.push(formatQuantity(row.adjustmentOutQty ?? row.AdjustmentOutQty));
      if (visibleColumns.totalOut) cells.push(formatQuantity(row.totalOut ?? row.TotalOut));
      // Closing
      if (visibleColumns.currentStock) cells.push(formatQuantity(row.currentStock ?? row.CurrentStock));
      if (visibleColumns.costPerUnit) {
        const v = row.costPerUnit ?? row.CostPerUnit;
        cells.push(v != null ? `${getCurrencySymbol(compCurrency)}${parseFloat(v).toFixed(2)}` : "-");
      }
      if (visibleColumns.stockValue) {
        const v = row.stockValue ?? row.StockValue;
        cells.push(v != null ? `${getCurrencySymbol(compCurrency)}${parseFloat(v).toFixed(2)}` : "-");
      }
      return cells;
    });
    return { headers, rows };
  };

  const exportToPDF = async () => {
    if (totalRecords === 0) {
      showErrorToast("No data to export.");
      return;
    }
    setExporting(true);
    try {
      const allData = await fetchAllReportDataForExport();
      if (allData.length === 0) {
        showErrorToast("No data to export.");
        return;
      }
      const { headers, rows } = getExportData(allData);
      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      doc.setFontSize(14);
      doc.text("Stock Overview", 14, 12);
      doc.setFontSize(10);
      doc.text(`Date: ${moment().format("DD/MM/YYYY HH:mm")} | Total records: ${rows.length}`, 14, 18);
      doc.autoTable({
        head: [headers],
        body: rows,
        startY: 22,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [66, 66, 66] },
      });
      const filename = `Inventory-Report-${moment().format("YYYYMMDD-HHmmss")}.pdf`;
      doc.save(filename);
      showSuccessToast(`PDF downloaded successfully (${rows.length} records)`);
    } catch (err) {
      console.error("Export PDF error:", err);
      showErrorToast(err.response?.data?.message || err.message || "Failed to generate PDF.");
    } finally {
      setExporting(false);
    }
  };

  const exportToXLSX = async () => {
    if (totalRecords === 0) {
      showErrorToast("No data to export.");
      return;
    }
    setExporting(true);
    try {
      const allData = await fetchAllReportDataForExport();
      if (allData.length === 0) {
        showErrorToast("No data to export.");
        return;
      }
      const { headers, rows } = getExportData(allData);
      const wb = XLSX.utils.book_new();
      const wsData = [headers, ...rows];
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      XLSX.utils.book_append_sheet(wb, ws, "Stock Overview");
      const filename = `Inventory-Report-${moment().format("YYYYMMDD-HHmmss")}.xlsx`;
      XLSX.writeFile(wb, filename);
      showSuccessToast(`Excel file downloaded successfully (${rows.length} records)`);
    } catch (err) {
      console.error("Export XLSX error:", err);
      showErrorToast(err.response?.data?.message || err.message || "Failed to generate Excel file.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="#/">Home</a></li>
            <li className="breadcrumb-item">Inventory Management</li>
            <li className="breadcrumb-item active">Stock Overview</li>
          </ol>
          <h1 className="page-header mb-0">Stock Overview</h1>
        </div>
      </div>

      <div
        className="card mb-4 master-inv-report-filters"
        style={{
          position: "relative",
          zIndex: 10,
          background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
          borderRadius: "12px",
          border: "none",
          boxShadow: `0 4px 20px ${themeColor}40`,
        }}
      >
        <div className="card-body px-3 py-3 px-md-4 py-md-4">
          <div className="master-inv-report-toolbar">
            <InventoryReportPeriodPicker
              variant="toolbar"
              themeColor={themeColor}
              dateRangePreset={dateRangePreset}
              rawStartDate={rawStartDate}
              rawEndDate={rawEndDate}
              compTimeZone={compTimeZone}
              compUTCTimeZone={compUTCTimeZone}
              onPresetClick={applyDatePreset}
              onApplyCustomStart={applyCustomStart}
              onClearQuick={clearQuickSelectToDefault}
              onStartToNowDateChange={handleStartToNowInputChange}
            />

            {isMain && locationOptions.length > 0 && (
              <div className="master-inv-report-toolbar__field master-inv-report-toolbar__location">
                <Select
                  value={resolvedLocation}
                  onChange={(opt) => handleFilterChange("location", opt)}
                  options={locationOptions}
                  styles={dropdownStyles}
                  isSearchable={false}
                  isLoading={loadingLocations}
                  placeholder="Location"
                  menuPosition="absolute"
                  className="w-100"
                  classNamePrefix="react-select"
                />
              </div>
            )}

            <div className="master-inv-report-toolbar__field master-inv-report-toolbar__search">
              <input
                type="text"
                className="filter-input w-100 master-inv-report-search"
                placeholder="Search item name, SKU..."
                value={search}
                onChange={handleSearchChange}
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              />
            </div>

            <div className="master-inv-report-toolbar__sort-group">
              <div className="master-inv-report-toolbar__field master-inv-report-toolbar__sort">
                <Select
                  value={sortBy}
                  onChange={(val) => dispatch(setSortBy({ pageKey, sortBy: val }))}
                  options={sortByOptions}
                  styles={dropdownStyles}
                  placeholder="Sort By"
                  menuPosition="absolute"
                  className="w-100"
                  classNamePrefix="react-select"
                />
              </div>
              <div className="master-inv-report-toolbar__field master-inv-report-toolbar__order">
                <SortOrderDropDownSelection
                  defaultValue={sortOrder}
                  onChange={(val) => dispatch(setSortOrder({ pageKey, sortOrder: val }))}
                  disableMenuPortal
                />
              </div>
            </div>

            <div className="master-inv-report-toolbar__actions">
              <button
                type="button"
                className="btn btn-sm btn-danger master-inv-report-action-btn"
                onClick={exportToPDF}
                title="Download full report as PDF"
                disabled={totalRecords === 0 || exporting}
              >
                <i className="fa fa-file-pdf me-1"></i> {exporting ? "..." : "PDF"}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-success master-inv-report-action-btn"
                onClick={exportToXLSX}
                title="Download full report as Excel"
                disabled={totalRecords === 0 || exporting}
              >
                <i className="fa fa-file-excel me-1"></i> {exporting ? "..." : "XLSX"}
              </button>
              <button
                type="button"
                className="clear-btn master-inv-report-action-btn"
                onClick={clearAllFilters}
                title="Clear all filters"
              >
                <i className="fa fa-times me-1"></i> Clear
              </button>
              <ColumnSelection
                visibleColumns={visibleColumns}
                setVisibleColumns={(cols) => dispatch(setVisibleColumns({ pageKey, columns: cols }))}
                columnDefinitions={masterInventoryColumnDefinitions}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="mb-3 mt-2">
        <div className="card border-0">
          <div className="tab-content p-3">
            <div className="tab-pane fade show active" id="allTab">
              <div
                className="table-responsive mt-3"
                style={{ minHeight: "200px", maxHeight: "450px", overflowY: "auto", overflowX: "auto" }}
              >
                <table className="table table-hover table-sm">
                  <thead
                    className="bg-light"
                    style={{
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                      backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                      color: isDarkMode ? "#ffffff" : "#000000",
                    }}
                  >
                    <tr>
                      {visibleColumns.itemCode && <th className="pt-2 pb-2">Code</th>}
                      {visibleColumns.itemName && <th className="pt-2 pb-2">Item Name</th>}
                      {visibleColumns.categoryName && <th className="pt-2 pb-2">Category</th>}
                      {visibleColumns.unitOfMeasure && <th className="pt-2 pb-2 text-center">Unit</th>}
                      {visibleColumns.openingStock && <th className="pt-2 pb-2 text-center">Opening</th>}
                      {/* Stock IN columns */}
                      {visibleColumns.purchaseQty && <th className="pt-2 pb-2 text-center" style={{ backgroundColor: isDarkMode ? "#1e5631" : "#d4edda" }}>Purchase</th>}
                      {visibleColumns.transferInQty && <th className="pt-2 pb-2 text-center" style={{ backgroundColor: isDarkMode ? "#1e5631" : "#d4edda" }}>Transfer In</th>}
                      {visibleColumns.adjustmentInQty && <th className="pt-2 pb-2 text-center" style={{ backgroundColor: isDarkMode ? "#1e5631" : "#d4edda" }}>Adjust In</th>}
                      {visibleColumns.recipeBatchInQty && <th className="pt-2 pb-2 text-center" style={{ backgroundColor: isDarkMode ? "#1e5631" : "#d4edda" }}>Batch Prod In</th>}
                      {visibleColumns.saleReturnInQty && <th className="pt-2 pb-2 text-center" style={{ backgroundColor: isDarkMode ? "#1e5631" : "#d4edda" }}>Return In</th>}
                      {visibleColumns.totalIn && <th className="pt-2 pb-2 text-center" style={{ backgroundColor: isDarkMode ? "#155724" : "#c3e6cb", fontWeight: 600 }}>Total In</th>}
                      {/* Stock OUT columns */}
                      {visibleColumns.salesAndProductionQty && (
                        <th className="pt-2 pb-2 text-center" style={{ backgroundColor: isDarkMode ? "#721c24" : "#f8d7da" }}>
                          Sales &amp; Production
                        </th>
                      )}
                      {visibleColumns.transferOutQty && <th className="pt-2 pb-2 text-center" style={{ backgroundColor: isDarkMode ? "#721c24" : "#f8d7da" }}>Transfer Out</th>}
                      {visibleColumns.adjustmentOutQty && <th className="pt-2 pb-2 text-center" style={{ backgroundColor: isDarkMode ? "#721c24" : "#f8d7da" }}>Adjust Out</th>}
                      {visibleColumns.totalOut && <th className="pt-2 pb-2 text-center" style={{ backgroundColor: isDarkMode ? "#5a1018" : "#f5c6cb", fontWeight: 600 }}>Total Out</th>}
                      {/* Closing */}
                      {visibleColumns.currentStock && <th className="pt-2 pb-2 text-center" style={{ backgroundColor: isDarkMode ? "#004085" : "#cce5ff", fontWeight: 600 }}>Current</th>}
                      {visibleColumns.costPerUnit && <th className="pt-2 pb-2 text-center">Cost</th>}
                      {visibleColumns.stockValue && <th className="pt-2 pb-2 text-center">Value</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={Object.keys(visibleColumns).filter((k) => visibleColumns[k]).length} className="text-center py-4">
                          <div className="spinner-border spinner-border-sm text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <span className="ms-2">Loading...</span>
                        </td>
                      </tr>
                    ) : reportData.length === 0 ? (
                      <tr>
                        <td colSpan={Object.keys(visibleColumns).filter((k) => visibleColumns[k]).length} className="text-center py-4">
                          <div className="text-muted">
                            <i className="fa fa-search fa-2x mb-2"></i>
                            <p className="mb-0">
                              {search.trim() ? "No inventory records found matching your search." : "No inventory data available."}
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      reportData.map((row, index) => (
                        <tr key={row.reportRowId ?? row.ReportRowId ?? index}>
                          {visibleColumns.itemCode && (
                            <td className="align-middle">{row.itemCode ?? row.ItemCode ?? "-"}</td>
                          )}
                          {visibleColumns.itemName && (
                            <td className="align-middle">{row.itemName ?? row.ItemName ?? "-"}</td>
                          )}
                          {visibleColumns.categoryName && (
                            <td className="align-middle">{row.categoryName ?? row.CategoryName ?? "-"}</td>
                          )}
                          {visibleColumns.unitOfMeasure && (
                            <td className="align-middle text-center">{row.unitOfMeasure ?? row.UnitOfMeasure ?? "-"}</td>
                          )}
                          {visibleColumns.openingStock && (
                            <td className="align-middle text-center">{formatQuantity(row.openingStock ?? row.OpeningStock)}</td>
                          )}
                          {/* Stock IN cells */}
                          {visibleColumns.purchaseQty && (
                            <td className="align-middle text-center text-success">{formatQuantity(row.purchaseQty ?? row.PurchaseQty)}</td>
                          )}
                          {visibleColumns.transferInQty && (
                            <td className="align-middle text-center text-success">{formatQuantity(row.transferInQty ?? row.TransferInQty)}</td>
                          )}
                          {visibleColumns.adjustmentInQty && (
                            <td className="align-middle text-center text-success">{formatQuantity(row.adjustmentInQty ?? row.AdjustmentInQty)}</td>
                          )}
                          {visibleColumns.recipeBatchInQty && (
                            <td className="align-middle text-center text-success">{formatQuantity(row.recipeBatchInQty ?? row.RecipeBatchInQty)}</td>
                          )}
                          {visibleColumns.saleReturnInQty && (
                            <td className="align-middle text-center text-success">{formatQuantity(row.saleReturnInQty ?? row.SaleReturnInQty)}</td>
                          )}
                          {visibleColumns.totalIn && (
                            <td className="align-middle text-center fw-bold text-success">{formatQuantity(row.totalIn ?? row.TotalIn)}</td>
                          )}
                          {/* Stock OUT cells */}
                          {visibleColumns.salesAndProductionQty && (
                            <td className="align-middle text-center text-danger">
                              {formatQuantity(getSalesAndProductionQty(row))}
                            </td>
                          )}
                          {visibleColumns.transferOutQty && (
                            <td className="align-middle text-center text-danger">{formatQuantity(row.transferOutQty ?? row.TransferOutQty)}</td>
                          )}
                          {visibleColumns.adjustmentOutQty && (
                            <td className="align-middle text-center text-danger">{formatQuantity(row.adjustmentOutQty ?? row.AdjustmentOutQty)}</td>
                          )}
                          {visibleColumns.totalOut && (
                            <td className="align-middle text-center fw-bold text-danger">{formatQuantity(row.totalOut ?? row.TotalOut)}</td>
                          )}
                          {/* Closing */}
                          {visibleColumns.currentStock && (
                            <td className="align-middle text-center fw-bold text-primary">{formatQuantity(row.currentStock ?? row.CurrentStock)}</td>
                          )}
                          {visibleColumns.costPerUnit && (
                            <td className="align-middle text-center">
                              {row.costPerUnit != null || row.CostPerUnit != null
                                ? `${getCurrencySymbol(compCurrency)}${parseFloat(row.costPerUnit ?? row.CostPerUnit ?? 0).toFixed(2)}`
                                : "-"}
                            </td>
                          )}
                          {visibleColumns.stockValue && (
                            <td className="align-middle text-center">
                              {row.stockValue != null || row.StockValue != null
                                ? `${getCurrencySymbol(compCurrency)}${parseFloat(row.stockValue ?? row.StockValue ?? 0).toFixed(2)}`
                                : "-"}
                            </td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination
                totalRecords={totalRecords}
                pageSize={pageSize}
                pageNumber={pageNumber}
                totalPages={totalPages}
                setPageNumber={setPageNumber}
                setPageSize={(val) => {
                  dispatch(setFilterField({ pageKey, field: "pageSize", value: val }));
                  setPageNumber(1);
                }}
                themeColor={themeColor}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MasterInventoryReport;
